import { useEffect, useState } from "react";
import { Shield, CheckCircle2, Circle } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

// Sprint 2H P5 — Admin jurisdictions page.
// NOTE: legal_signoff structural enforcement (backend refuses live=true when
// legal_signoff is null) is DEFERRED to the pre-launch checklist per user
// directive. See PRD "Pre-Launch Checklist" for the 1-line backend change.

export default function AdminJurisdictions() {
  const [rows, setRows] = useState(null);
  const [pending, setPending] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/jurisdictions");
      setRows(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    if (!pending) return;
    setSubmitting(true);
    try {
      await api.patch(`/admin/jurisdictions/${pending.jurisdiction_id}`, { live: !pending.live });
      toast.success(`${pending.state}/${pending.regulation_module} live=${!pending.live}`);
      setPending(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8" data-testid="admin-jurisdictions-page">
        <div className="flex items-center gap-2 mb-6">
          <Shield size={22} style={{ color: "var(--blue)" }} />
          <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }} data-testid="admin-jurisdictions-heading">
            Jurisdictions
          </h1>
        </div>
        <p className="text-sm mb-5" style={{ color: "var(--slate)" }}>
          Toggle LIVE per (state, regulation module). Non-live jurisdictions return 451
          on all regulated writes for their schemes.
        </p>

        <div className="rounded-xl bg-white border overflow-hidden" style={{ borderColor: "var(--border)" }}>
          {!rows ? (
            <div className="p-5 space-y-2">{[0,1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : (
            <table className="w-full text-sm">
              <thead style={{ background: "#f8fafc" }}>
                <tr className="text-[11px] uppercase tracking-wider" style={{ color: "var(--slate)" }}>
                  <th className="text-left px-4 py-2 font-semibold">State</th>
                  <th className="text-left px-4 py-2 font-semibold">Regulation module</th>
                  <th className="text-left px-4 py-2 font-semibold">Live</th>
                  <th className="text-left px-4 py-2 font-semibold">Signed off by</th>
                  <th className="text-left px-4 py-2 font-semibold">Signed off at</th>
                  <th className="text-right px-4 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((j) => (
                  <tr key={j.jurisdiction_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`jur-row-${j.state}-${j.regulation_module}`}>
                    <td className="px-4 py-3 font-semibold" style={{ color: "var(--navy)" }}>{j.state}</td>
                    <td className="px-4 py-3" style={{ color: "var(--slate)" }}>{j.regulation_module}</td>
                    <td className="px-4 py-3">
                      {j.live ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "#166534" }}>
                          <CheckCircle2 size={12} /> LIVE
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--slate)" }}>
                          <Circle size={12} /> off
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--slate)" }}>{j.signed_off_by_email || "—"}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--slate)" }}>
                      {j.signed_off_at ? String(j.signed_off_at).slice(0,10) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => setPending(j)}
                        data-testid={`jur-toggle-${j.state}-${j.regulation_module}`}
                      >
                        {j.live ? "Disable" : "Enable"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
          <DialogContent data-testid="jur-toggle-modal">
            <DialogHeader>
              <DialogTitle>
                {pending?.live ? "Disable" : "Enable"} {pending?.state} / {pending?.regulation_module}?
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm" style={{ color: "var(--slate)" }}>
              {pending?.live
                ? "All regulated writes for schemes in this jurisdiction will start returning 451 immediately."
                : "This will unblock regulated actions (compliance, meetings, levies, by-laws) for all schemes in this jurisdiction."}
            </p>
            {!pending?.live && (
              <div className="rounded-md p-3 text-xs" style={{ background: "#fef3c7", color: "#78350f", borderLeft: "3px solid #f59e0b" }}>
                <strong>Pre-launch reminder:</strong> before enabling LIVE in production, the
                backend must refuse this call when legal_signoff is null. That constraint is
                DEFERRED to pre-launch per current directive.
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setPending(null)}>Cancel</Button>
              <Button
                onClick={toggle} disabled={submitting}
                style={{ background: pending?.live ? "#991b1b" : "var(--blue)", color: "white" }}
                data-testid="jur-toggle-confirm"
              >
                {submitting ? "…" : (pending?.live ? "Disable" : "Enable")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
